import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import SearchIcon from '@mui/icons-material/Search'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Drawer,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import DOMPurify from 'dompurify'
import { useEffect, useMemo, useState } from 'react'
import client from 'axiosConfig'
import FaqServices, { FaqItem } from 'app/services/api/faq/faq.services'

interface FaqDrawerProps {
  open: boolean
  onClose: () => void
}

function escapeRegex(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function HighlightText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <span>{text}</span>
  const parts = text.split(new RegExp(`(${escapeRegex(query)})`, 'gi'))
  const occurrences = new Map<string, number>()

  return (
    <>
      {parts.map((part) => {
        const occurrence = occurrences.get(part) ?? 0
        occurrences.set(part, occurrence + 1)
        const key = `${part}-${occurrence}`

        return part.toLowerCase() === query.toLowerCase() ? (
          <mark
            key={key}
            style={{
              backgroundColor: '#fff176',
              borderRadius: 2,
              padding: '0 2px',
            }}
          >
            {part}
          </mark>
        ) : (
          <span key={key}>{part}</span>
        )
      })}
    </>
  )
}

function highlightHtml(html: string, query: string): string {
  if (!query.trim()) return html
  const escaped = escapeRegex(query)
  // Alternate match: HTML tags are returned unchanged; search term gets wrapped
  return html.replace(new RegExp(`(<[^>]*>|${escaped})`, 'gi'), (match) => {
    if (match.startsWith('<')) return match
    return `<mark class="faq-hl">${match}</mark>`
  })
}

export function resolveFaqImageUrls(
  html: string,
  baseUrl = client.defaults.baseURL
): string {
  if (!baseUrl || typeof document === 'undefined') return html

  const template = document.createElement('template')
  template.innerHTML = html
  const absoluteBaseUrl = new URL(baseUrl, window.location.origin)

  template.content
    .querySelectorAll<HTMLImageElement>('img[src^="/api/v1/"]')
    .forEach((image) => {
      const src = image.getAttribute('src')
      if (src) image.setAttribute('src', new URL(src, absoluteBaseUrl).href)
    })

  return template.innerHTML
}

const DRAWER_WIDTH = '50vw'

function FaqSkeleton() {
  return (
    <Box sx={{ px: 2, py: 1 }}>
      {[1, 2, 3, 4].map((n) => (
        <Box
          key={n}
          sx={{
            mb: 1.5,
            p: 2,
            borderRadius: 2,
            bgcolor: '#f5f5f5',
            border: '1px solid #e0e0e0',
          }}
        >
          <Skeleton variant='text' width='80%' height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant='text' width='40%' height={16} />
        </Box>
      ))}
    </Box>
  )
}

export function FaqDrawer({ open, onClose }: FaqDrawerProps) {
  const [faqs, setFaqs] = useState<FaqItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  useEffect(() => {
    if (!search.trim()) {
      setExpanded(new Set())
      return
    }
    const q = search.toLowerCase()
    const ids = faqs
      .filter((f) => f.answer.toLowerCase().includes(q))
      .map((f) => f.id)
    setExpanded(new Set(ids))
  }, [search, faqs])

  useEffect(() => {
    if (!open) return
    setLoading(true)
    setError(false)
    FaqServices.getFaqs()
      .then((res) => {
        setFaqs(res.data.data ?? [])
      })
      .catch(() => {
        setError(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [open])

  const filtered = useMemo(() => {
    if (!search.trim()) return faqs
    const q = search.toLowerCase()
    return faqs.filter(
      (f) =>
        f.question.toLowerCase().includes(q) ||
        f.answer.toLowerCase().includes(q)
    )
  }, [faqs, search])

  const handleAccordionChange =
    (id: number) => (_: unknown, isExpanded: boolean) => {
      setExpanded((prev) => {
        const next = new Set(prev)
        if (isExpanded) next.add(id)
        else next.delete(id)
        return next
      })
    }

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      variant='temporary'
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          background: 'linear-gradient(135deg, #0000A0 0%, #1a1aff 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: 22 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant='subtitle1' fontWeight={700} lineHeight={1.2}>
            Help Center
          </Typography>
          <Typography variant='caption' sx={{ opacity: 0.8 }}>
            Frequently asked questions
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size='small'
          sx={{
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
          }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>

      {/* Search */}
      <Box sx={{ px: 2, pt: 2, pb: 1, flexShrink: 0 }}>
        <TextField
          fullWidth
          size='small'
          placeholder='Search questions…'
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position='end'>
                <IconButton
                  size='small'
                  onClick={() => setSearch('')}
                  edge='end'
                  aria-label='clear search'
                >
                  <CloseIcon fontSize='small' />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: '#f5f5f5',
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: '1px solid #bbb' },
              '&.Mui-focused fieldset': { border: '1px solid #0000A0' },
            },
          }}
        />
      </Box>

      {/* Count indicator */}
      {!loading && !error && (
        <Typography
          variant='caption'
          sx={{ px: 2.5, pb: 0.5, color: 'text.secondary', flexShrink: 0 }}
        >
          {search.trim()
            ? `${filtered.length} result${
                filtered.length !== 1 ? 's' : ''
              } for "${search}"`
            : `${faqs.length} article${faqs.length !== 1 ? 's' : ''}`}
        </Typography>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 2 }}>
        {loading && <FaqSkeleton />}

        {!loading && error && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60%',
              gap: 1,
              color: 'text.secondary',
            }}
          >
            <HelpOutlineIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            <Typography variant='body2' textAlign='center'>
              Could not load FAQs.
              <br />
              Please try again later.
            </Typography>
          </Box>
        )}

        {!loading && !error && filtered.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60%',
              gap: 1,
              color: 'text.secondary',
            }}
          >
            <SearchIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            <Typography variant='body2' textAlign='center'>
              {search.trim()
                ? `No results for "${search}"`
                : 'No FAQs available'}
            </Typography>
          </Box>
        )}

        {!loading &&
          !error &&
          filtered.map((faq) => (
            <Accordion
              key={faq.id}
              expanded={expanded.has(faq.id)}
              onChange={handleAccordionChange(faq.id)}
              disableGutters
              elevation={0}
              sx={{
                mb: 1,
                borderRadius: '10px !important',
                border: '1px solid',
                borderColor: expanded.has(faq.id)
                  ? '#0000A0'
                  : 'rgba(0,0,0,0.08)',
                overflow: 'hidden',
                '&:before': { display: 'none' },
                transition: 'border-color 0.2s',
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      color: expanded.has(faq.id)
                        ? '#0000A0'
                        : 'text.secondary',
                      transition: 'color 0.2s',
                    }}
                  />
                }
                sx={{
                  px: 2,
                  py: 1,
                  minHeight: 'unset',
                  bgcolor: expanded.has(faq.id) ? 'rgba(0,0,160,0.04)' : '#fff',
                  transition: 'background-color 0.2s',
                  '& .MuiAccordionSummary-content': { my: 0.75 },
                }}
              >
                <Typography
                  variant='body2'
                  fontWeight={expanded.has(faq.id) ? 600 : 500}
                  sx={{
                    color: expanded.has(faq.id) ? '#0000A0' : 'text.primary',
                    lineHeight: 1.4,
                  }}
                >
                  <HighlightText text={faq.question} query={search} />
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  px: 2,
                  pt: 0,
                  pb: 2,
                  bgcolor: '#fff',
                  borderTop: '1px solid rgba(0,0,160,0.08)',
                }}
              >
                <Box
                  className='faq-answer'
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      highlightHtml(resolveFaqImageUrls(faq.answer), search),
                      {
                        ADD_TAGS: ['mark'],
                        ADD_ATTR: ['class', 'colspan', 'rowspan', 'style'],
                      }
                    ),
                  }}
                  sx={{
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    lineHeight: 1.6,

                    // --- Block elements ---
                    '& p': { mt: 0, mb: 1 },
                    '& p:last-child': { mb: 0 },

                    // --- Headings ---
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      mt: 1.5,
                      mb: 0.75,
                      color: 'text.primary',
                      fontWeight: 600,
                      lineHeight: 1.3,
                    },
                    '& h1': { fontSize: '1.4rem' },
                    '& h2': { fontSize: '1.2rem' },
                    '& h3': { fontSize: '1.05rem' },
                    '& h4, & h5, & h6': { fontSize: '0.9375rem' },

                    // --- Lists ---
                    '& ul, & ol': { pl: 2.5, mt: 0.5, mb: 1 },
                    '& li': { mb: 0.5 },
                    // CKEditor 5 todo list
                    '& ul.todo-list': { listStyle: 'none', pl: 0 },
                    '& ul.todo-list .todo-list__label > input': { mr: 0.75 },

                    // --- Inline formatting ---
                    '& strong': { color: 'text.primary', fontWeight: 600 },
                    '& em': { fontStyle: 'italic' },
                    '& u': { textDecoration: 'underline' },
                    '& s': { textDecoration: 'line-through' },
                    '& sub': { verticalAlign: 'sub', fontSize: '0.75em' },
                    '& sup': { verticalAlign: 'super', fontSize: '0.75em' },

                    // --- CKEditor 5 font size classes ---
                    '& .text-tiny': { fontSize: '0.7em' },
                    '& .text-small': { fontSize: '0.85em' },
                    '& .text-big': { fontSize: '1.15em' },
                    '& .text-huge': { fontSize: '1.4em' },

                    // --- Links ---
                    '& a': {
                      color: '#0000A0',
                      textDecorationColor: 'rgba(0,0,160,0.4)',
                    },
                    '& a:hover': { textDecorationColor: '#0000A0' },

                    // --- Blockquote ---
                    '& blockquote': {
                      borderLeft: '4px solid rgba(0,0,160,0.3)',
                      ml: 0,
                      pl: 2,
                      fontStyle: 'italic',
                      my: 1,
                      color: 'text.secondary',
                    },

                    // --- Code ---
                    '& pre': {
                      backgroundColor: '#f5f5f5',
                      borderRadius: '6px',
                      padding: '10px 14px',
                      overflowX: 'auto',
                      fontSize: '0.8rem',
                      my: 1,
                      border: '1px solid rgba(0,0,0,0.08)',
                    },
                    '& code': {
                      fontFamily: 'monospace',
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      borderRadius: '3px',
                      padding: '1px 4px',
                      fontSize: '0.85em',
                    },
                    '& pre code': {
                      backgroundColor: 'transparent',
                      padding: 0,
                    },

                    // --- Horizontal rule ---
                    '& hr': {
                      border: 'none',
                      borderTop: '1px solid rgba(0,0,0,0.12)',
                      my: 1.5,
                    },

                    // --- Images ---
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 1,
                    },
                    // CKEditor 5 figure.image wrapper
                    '& figure.image': { display: 'table', my: 1, mx: 'auto' },
                    '& figure.image img': {
                      display: 'block',
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 1,
                    },
                    '& figure.image.image-style-side': {
                      float: 'right',
                      maxWidth: '50%',
                      ml: 2,
                      mb: 1,
                    },
                    '& figure.image.image-style-align-left': {
                      float: 'left',
                      mr: 2,
                      mb: 1,
                    },
                    '& figure.image.image-style-align-right': {
                      float: 'right',
                      ml: 2,
                      mb: 1,
                    },
                    '& figure figcaption': {
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      textAlign: 'center',
                      mt: 0.5,
                      fontStyle: 'italic',
                    },

                    // --- Tables ---
                    // CKEditor 5 wraps tables in <figure class="table">
                    '& figure.table': { overflowX: 'auto', my: 1.5, mx: 0 },
                    '& figure.table table': { margin: 0 },
                    '& table': {
                      borderCollapse: 'collapse',
                      width: '100%',
                      fontSize: 'inherit',
                    },
                    '& th, & td': {
                      border: '1px solid rgba(0,0,0,0.2)',
                      padding: '6px 12px',
                      textAlign: 'left',
                      verticalAlign: 'top',
                    },
                    '& th': {
                      backgroundColor: 'rgba(0,0,160,0.06)',
                      fontWeight: 600,
                      color: 'text.primary',
                    },
                    '& tr:nth-of-type(even)': {
                      backgroundColor: 'rgba(0,0,0,0.02)',
                    },
                    // CKEditor 5 table caption
                    '& figure.table figcaption': {
                      caption: 'bottom',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      textAlign: 'center',
                      mt: 0.5,
                    },

                    // --- Media embed ---
                    '& figure.media': { my: 1 },
                    '& figure.media oembed': { display: 'none' },

                    // --- Search highlight ---
                    '& mark.faq-hl': {
                      backgroundColor: '#fff176',
                      borderRadius: '2px',
                      padding: '0 2px',
                      color: 'inherit',
                    },
                  }}
                />
              </AccordionDetails>
            </Accordion>
          ))}
      </Box>
    </Drawer>
  )
}
